import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Drawer from "@material-ui/core/Drawer";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Close from "@material-ui/icons/Close";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Button from "@material-ui/core/Button";
import HomeIcon from "@material-ui/icons/HomeOutlined";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import Loading from "../../Loading";
import { useQuery } from "react-query";
import { useTokenQuery } from "../../../hooks/useTokenQuery";
import { fetchPlayerStats } from "./PlayerStatsService";
import { useRecoilValue } from "recoil";
import { comparePlayerStatsDataState } from "../../../Atoms/uiAtom";

const ComparePlayerStats = ({ open, onClose }) => {
  const classes = useStyles();
  const {
    marketIds,
    statIds,
    selectedStatId: initialStatId,
  } = useRecoilValue(comparePlayerStatsDataState);
  const [currentPlayerOneDisplayStats, setCurrentPlayerOneDisplayStats] =
    useState("playerOneAll");
  const [selectedLastGames, setSelectedLastGames] = useState(5);
  const [averagePlayer1, setAveragePlayer1] = useState(0);
  const [averagePlayer2, setAveragePlayer2] = useState(0);
  const [playerStatsData, setPlayerStatsData] = useState(null);
  const [selectedMarketId, setSelectedMarketId] = useState(null);
  const [selectedStatId, setSelectedStatId] = useState(initialStatId);

  const tokenQuery = useTokenQuery();

  // When marketIds are updated, reset playerStats
  useEffect(() => {
    if (!initialStatId) {
      setPlayerStatsData(null);
      setSelectedStatId(null);
      setSelectedMarketId(null);
    }
  }, [marketIds, initialStatId]);

  const toTitleCase = (str) => {
    return str
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, function (str) {
        return str.toUpperCase();
      });
  };

  // unique player stats as marketId and setId contain duplicates
  const getUniqueComparePlayerStatsData = () => {
    const uniqueStatIds = new Set();
    const uniqueData = [];

    for (let i = 0; i < marketIds.length; i++) {
      if (uniqueStatIds.has(statIds[i])) {
        break;
      }
      uniqueStatIds.add(statIds[i]);
      uniqueData.push({
        marketId: marketIds[i],
        statId: statIds[i],
        statLabel: toTitleCase(statIds[i]),
      });
    }

    return uniqueData;
  };

  const uniqueComparePlayerStatsData = useMemo(
    getUniqueComparePlayerStatsData,
    [marketIds, statIds],
  );

  const { isLoading, error, data } = useQuery(
    ["playerStatsHistory", selectedMarketId, selectedStatId],
    () => {
      return fetchPlayerStats(selectedMarketId, selectedStatId, tokenQuery);
    },
    {
      enabled: !!selectedMarketId && !!selectedStatId && !!tokenQuery.data,
      onSuccess: (data) => {
        setPlayerStatsData(data);
      },
      onError: (error) => {
        console.error("Error:", error);
      },
    },
  );

  // Add an effect to update selectedStatId when initialStatId changes
  useEffect(() => {
    if (initialStatId) {
      setSelectedStatId(initialStatId);
      // Find the corresponding marketId for this statId
      const matchingData = uniqueComparePlayerStatsData.find(
        (data) => data.statId === initialStatId,
      );
      if (matchingData) {
        setSelectedMarketId(matchingData.marketId);
      }
    }
  }, [initialStatId, uniqueComparePlayerStatsData]);

  const handleLastGamesChange = (lastGames) => {
    setSelectedLastGames(lastGames);
  };

  const handleStatTypeChange = (statId, marketId) => {
    setSelectedStatId(statId);
    setSelectedMarketId(marketId);
  };

  const arePlayersOnSameTeam = () => {
    if (!playerStatsData) {
      return false;
    }

    const player1 = playerStatsData.player1;
    const player2 = playerStatsData.player2;

    if (!player1 || !player2) {
      return false;
    }

    const player1Team = player1.player && player1.player.team;
    const player2Team = player2.player && player2.player.team;

    if (!player1Team || !player2Team) {
      return false;
    }

    const sameTeam = player1Team.id === player2Team.id;

    return sameTeam;
  };

  const handleButtonOneClick = () => {
    if (currentPlayerOneDisplayStats === "playerOneAll") {
      setCurrentPlayerOneDisplayStats("playerOneHome");
    } else if (currentPlayerOneDisplayStats === "playerOneHome") {
      setCurrentPlayerOneDisplayStats("playerOneAway");
    } else {
      setCurrentPlayerOneDisplayStats("playerOneHome");
    }
  };

  const handleButtonThreeClick = () => {
    if (currentPlayerOneDisplayStats === "playerOneAll") {
      setCurrentPlayerOneDisplayStats("playerOneAway");
    } else if (currentPlayerOneDisplayStats === "playerOneHome") {
      setCurrentPlayerOneDisplayStats("playerOneAway");
    } else {
      setCurrentPlayerOneDisplayStats("playerOneHome");
    }
  };

  const getButtonOneLabel = () => {
    if (currentPlayerOneDisplayStats === "playerOneAll") {
      return "All";
    } else if (currentPlayerOneDisplayStats === "playerOneHome") {
      return "Home";
    } else {
      return "Away";
    }
  };

  const getButtonThreeLabel = () => {
    if (currentPlayerOneDisplayStats === "playerOneAll") {
      return "All";
    } else if (currentPlayerOneDisplayStats === "playerOneHome") {
      return "Away";
    } else {
      return "Home";
    }
  };

  useEffect(() => {
    if (playerStatsData) {
      const calculateAverage = (stats) => {
        if (!stats.length) return 0;
        const total = stats.reduce((acc, stat) => acc + stat.value, 0);
        return (total / stats.length).toFixed(2);
      };

      const filteredStatsPlayer1 = filterStats(
        playerStatsData.player1.allStatHistory,
        selectedLastGames,
        currentPlayerOneDisplayStats,
      );
      const filteredStatsPlayer2 = filterStats(
        playerStatsData.player2.allStatHistory,
        selectedLastGames,
        currentPlayerOneDisplayStats,
      );

      setAveragePlayer1(calculateAverage(filteredStatsPlayer1));
      setAveragePlayer2(calculateAverage(filteredStatsPlayer2));
    }
  }, [playerStatsData, selectedLastGames, currentPlayerOneDisplayStats]);

  const filterStats = (statHistory, selectedLastGames, displayType) => {
    // Filter based on displayType
    let filteredStats = statHistory.filter((stat) => {
      if (displayType === "playerOneHome") {
        return stat.isHome;
      } else if (displayType === "playerOneAway") {
        return !stat.isHome;
      }
      return true;
    });

    // Sort by date (most recent first) - Sorted at API level
    // filteredStats.sort(
    //   (a, b) => new Date(b.fixture.date) - new Date(a.fixture.date),
    // );

    // Take only the last selectedLastGames entries
    return filteredStats.slice(0, selectedLastGames);
  };

  const mapStatLines = (
    statHistory,
    selectedLastGames,
    displayType,
    reverse = false,
  ) => {
    const filteredStats = filterStats(
      statHistory,
      selectedLastGames,
      displayType,
    );

    return filteredStats.map((stat, index) => {
      const { isHome, value, fixture } = stat;
      const dateObj = new Date(fixture.date);
      const isValidDate = !isNaN(dateObj.getTime());

      const date = isValidDate
        ? new Intl.DateTimeFormat("en-GB", {
            day: "2-digit",
            month: "short",
            year: "2-digit",
          }).format(dateObj)
        : "Invalid Date";

      // Determine the home/away status based on whether the players are on the same team.
      // If they are on the same team, use the original isHome value. If they are on different teams, flip the isHome value if reverse is true.
      const homeStatus = arePlayersOnSameTeam()
        ? isHome
        : reverse
        ? !isHome
        : isHome;

      return (
        <StatLine
          key={index}
          home={homeStatus}
          score={value}
          date={date}
          reverse={reverse}
        />
      );
    });
  };

  // if (isLoading) {
  //   return <Loading />;
  // }

  // if (error) {
  //   return <div>Error: {error.message}</div>;
  // }

  return (
    <Drawer
      className={classes.playerStatDrawer}
      variant="persistent"
      anchor="left"
      open={open}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <Box className={classes.drawerHeader}>
        <Box flex="1">
          <Typography variant="subtitle1">Compare Player Stats</Typography>
        </Box>
        <IconButton aria-label="close" color="inherit" onClick={onClose}>
          <Close />
        </IconButton>
      </Box>
      <Box className={classes.drawerContent}>
        {/* Type of Stat Filter */}
        <Box className={classes.playerStatFilterBox}>
          <Typography variant="subtitle2">Stat Type</Typography>

          <Box className={classes.selectorBox}>
            {uniqueComparePlayerStatsData
              ? uniqueComparePlayerStatsData.map(
                  ({ marketId, statId, statLabel }) => (
                    <Chip
                      key={marketId}
                      label={statLabel}
                      onClick={() => handleStatTypeChange(statId, marketId)}
                      color={selectedStatId === statId ? "primary" : "default"}
                    />
                  ),
                )
              : "Stat Types cannot be loaded"}
          </Box>
        </Box>
        {/* Number of Games Filter */}
        <Box className={classes.playerStatFilterBox}>
          <Typography variant="subtitle2">Number of Games</Typography>

          <Box className={classes.selectorBox}>
            <Chip
              label="Last 5"
              onClick={() => handleLastGamesChange(5)}
              color={selectedLastGames === 5 ? "primary" : "default"}
            />
            <Chip
              label="Last 10"
              onClick={() => handleLastGamesChange(10)}
              color={selectedLastGames === 10 ? "primary" : "default"}
            />
          </Box>
        </Box>
        {/* Home Away Filter */}
        <Box className={classes.playerStatFilterBox}>
          <Typography variant="subtitle2">Home Away Filter</Typography>

          <Box className={classes.flexSpaceBetween}>
            <Button
              variant="contained"
              endIcon={<SwapHorizIcon />}
              className={classes.homeAwayButton}
              onClick={handleButtonOneClick}
              color={
                currentPlayerOneDisplayStats === "playerOneHome"
                  ? "primary"
                  : "default"
              }
            >
              {getButtonOneLabel()}
            </Button>
            <Button
              variant="contained"
              onClick={() => setCurrentPlayerOneDisplayStats("playerOneAll")}
              color={
                currentPlayerOneDisplayStats === "playerOneAll"
                  ? "primary"
                  : "default"
              }
            >
              All
            </Button>
            <Button
              variant="contained"
              startIcon={<SwapHorizIcon />}
              className={classes.homeAwayButton}
              onClick={
                arePlayersOnSameTeam
                  ? handleButtonThreeClick
                  : handleButtonOneClick
              }
              color={
                arePlayersOnSameTeam
                  ? currentPlayerOneDisplayStats === "playerOneAway"
                    ? "primary"
                    : "default"
                  : currentPlayerOneDisplayStats === "playerOneHome"
                  ? "default"
                  : "primary"
              }
            >
              {arePlayersOnSameTeam
                ? getButtonThreeLabel()
                : getButtonOneLabel()}
            </Button>
          </Box>
        </Box>
        {isLoading ? (
          <Loading />
        ) : error ? (
          <Typography color="error">Error: {error.message}</Typography>
        ) : playerStatsData ? (
          <>
            {/* Display Player Names */}
            <Box
              className={classes.flexSpaceBetween}
              style={{ minHeight: "32px", marginTop: "16px" }}
            >
              <Box flex="1">
                <Typography variant="subtitle1">
                  {playerStatsData.player1.player.name}
                </Typography>
              </Box>
              <Typography variant="subtitle1">vs</Typography>
              <Box flex="1" justifyContent="end" display="flex">
                <Typography variant="subtitle1">
                  {playerStatsData.player2.player.name}
                </Typography>
              </Box>
            </Box>
            {/* Display Results */}
            <Box className={classes.resultsBox}>
              <Box
                className={classes.flexSpaceBetween}
                style={{ alignItems: "start" }}
              >
                <Box flex="1" flexDirection="column" display="flex">
                  {mapStatLines(
                    playerStatsData.player1.allStatHistory,
                    selectedLastGames,
                    currentPlayerOneDisplayStats,
                  )}
                </Box>
                <Box
                  flex="1"
                  flexDirection="column"
                  alignItems="end"
                  display="flex"
                >
                  {mapStatLines(
                    playerStatsData.player2.allStatHistory,
                    selectedLastGames,
                    currentPlayerOneDisplayStats,
                    true,
                  )}
                </Box>
              </Box>
            </Box>
            {/* Display Average of Results */}
            <Box
              className={classes.flexSpaceBetween}
              style={{ minHeight: "28px" }}
            >
              <Box flex="1">
                <Typography
                  className={classes.statLineScoreDisplay}
                  variant="subtitle1"
                >
                  {averagePlayer1}
                </Typography>
              </Box>
              <Typography
                className={classes.statLineDateDisplay}
                variant="subtitle1"
                style={{ minWidth: "unset" }}
              >
                Average
              </Typography>
              <Box flex="1" justifyContent="end" display="flex">
                <Typography
                  className={classes.statLineScoreDisplay}
                  variant="subtitle1"
                >
                  {averagePlayer2}
                </Typography>
              </Box>
            </Box>
          </>
        ) : (
          <Typography className={classes.errorMessage}>
            No player stats data: Try selecting a stat Type
          </Typography>
        )}
      </Box>
    </Drawer>
  );
};

export default ComparePlayerStats;

const useStyles = makeStyles((theme) => ({
  statLineDateDisplay: {
    fontSize: "0.875rem",
    lineHeight: "24px",
    opacity: "0.9",
    fontWeight: "400",
    minWidth: "76px",
  },
  statLineRoot: {
    gap: "8px",
    alignItems: "center",
    minHeight: "28px",
  },
  statLineScoreDisplay: {
    lineHeight: "24px",
    minWidth: "28px",
  },

  statLineHomeContainer: {
    minWidth: "20px",
    display: "flex",
    alignItems: "center",
  },
  playerStatDrawer: {},
  drawerPaper: {
    width: "380px",
  },
  drawerHeader: {
    backgroundColor: theme.palette.background.default,
    display: "flex",
    alignItems: "center",
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0, 1, 0, 2),
    ...theme.mixins.toolbar,
    width: "100%",
  },
  drawerContent: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
  },
  flexSpaceBetween: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  playerStatFilterBox: {
    marginBottom: "8px",
  },
  selectorBox: {
    display: "flex",
    gap: "4px",
    flexWrap: "wrap",
    alignItems: "center",
    padding: "4px 0",
  },
  homeAwayButton: {
    minWidth: "96px",
  },
  errorMessage: {
    marginTop: "16px",
    // padding: "0 16px",
  },
}));

const StatLine = ({ home, score, date, reverse }) => {
  const classes = useStyles();

  return (
    <Box flexDirection="row" display="flex" className={classes.statLineRoot}>
      {reverse ? (
        <>
          <Box className={classes.statLineHomeContainer}>
            {home && <HomeIcon fontSize="small" />}
          </Box>
          <Typography
            className={classes.statLineDateDisplay}
            variant="caption"
            style={{ textAlign: "right" }}
          >
            {date}
          </Typography>

          <Typography
            className={classes.statLineScoreDisplay}
            variant="subtitle1"
            style={{ textAlign: "right" }}
          >
            {score}
          </Typography>
        </>
      ) : (
        <>
          <Typography
            className={classes.statLineScoreDisplay}
            variant="subtitle1"
          >
            {score}
          </Typography>
          <Typography className={classes.statLineDateDisplay} variant="caption">
            {date}
          </Typography>
          <Box className={classes.statLineHomeContainer}>
            {home && <HomeIcon fontSize="small" />}
          </Box>
        </>
      )}
    </Box>
  );
};
